import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import { UseFormRegister } from 'react-hook-form/dist/types/form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { InputHolder, InputHolderItem } from '../../shared/InputHolder';
import { formItems } from '../RefinanceStepper/formItems';
import { Select, SelectOption } from '../../shared/Select';
import { Input } from '../../shared/Input';
import { API } from '../../../services';
import { InterestResponse } from '../../../models/InterestResponse';
import { getMarketValuePercentage, getMortgageForm, isPercentage } from '../../../utils/validations';
import { FormContext } from '../../../store/refinance/RefinanceContext';
import { RemainingMortgagePart } from '../../../models/RemainingMortgageResponse';
import { floatToPercentage, numberToCurrency } from '../../../utils/formatting';
import { StepperContext } from '../../../store/stepper/StepperContext';
import { convertDateObject } from '../../../utils/dates';
import { parseISO } from 'date-fns';

export const MortgagePartInterest: FunctionComponent<{
	register: UseFormRegister<any>, errors: FieldErrors<any>, part: number, values?: any, setValue: any  }> = ({register, errors, part, values, setValue}) => {

	const [showExtraField, setShowExtraField] = useState(false);
	const [remainingMortgagePart, setRemainingMortgagePart] = useState<RemainingMortgagePart | null>(null);//{ residualDebt: 0, endDate: '', durationTermString: '', durationTerm: 0 });
	const [interests, setInterests] = useState<SelectOption[]>([]);
	const { form, remainingMortgage, setIsLoading } = useContext(FormContext);
	const { state } = useContext(StepperContext);
	const { steps } = state;

	useEffect(() => {
		if (remainingMortgage && !steps[0].isActive && !steps[1].isActive && !steps[2].isActive && steps[3].isActive) {
			setIsLoading(true);
			setRemainingMortgagePart(remainingMortgage.mortgageParts[part])
			API.get<InterestResponse>(`interestrates/getlowestinterestrates?RenteBasis=${getMarketValuePercentage(remainingMortgage?.marketValuePercentage, form[formItems.step3.mortgageWithNhg])}&HypotheekVorm=${getMortgageForm(parseInt(values[formItems.step2.mortgagePart.mortgageType]))}&IsNieuwbouw=false`)
				.then((res) => {

					const remainingYears = remainingMortgage.mortgageParts[part].durationTerm / 12;
					const resFiltered = res.filter((r, index) =>  {
						return r.period < Math.ceil(remainingYears)
					});

					// find index of last item of the resFiltered
					const indexOfLastItem = res.indexOf(resFiltered[resFiltered.length - 1]);
					// make sure the last item if the resFiltered was not the last item in the res object
					if (res.length > indexOfLastItem + 1) {
						resFiltered.push(res[indexOfLastItem + 1])
					}

					let interests: SelectOption[] = [];

					resFiltered.forEach((r, index) => interests.push({
						name: `${floatToPercentage(r.interestRate)}% - ${r.period > 0 ? `${r.period} jaar vast` : `variabele rente` }`,
						value: r.interestRate,
						key: `${part}-${index}-${r.interestRate}`
					}));

					interests.push({
						name: 'andere rente',
						value: 'OTHER_INTEREST',
					});

					setInterests(interests);
					setIsLoading(false);
			})
		}
	}, [remainingMortgage, steps, part, form, values, setIsLoading])

	useEffect(() => {
		if (interests.length) {
			setValue(`${formItems.step4.interest}.${part}.${formItems.step4.lowestInterest}`, interests[0].value, {shouldDirty: true, shouldValidate: true})
		}
	}, [interests, setValue, part])

	function getFormName(name: string): string {
		return `${formItems.step4.interest}.${part}.${name}`
	}

	function hasError(name: string) {
		if (typeof errors[`mortgage`] !== 'undefined') {
			return errors[`mortgage`][part][name];
		}
	}

	const getToolTip = (mortgageType: string, mortgageAmountStartFixedInterestRate: number, mortgageTermStart: string, residualDebt: number, durationTermString: string, endDate: string) => {
		return formItems.step4.toolTips.preferredInterestAndPeriod
			.replace(/{mortgageType}/g, mortgageType)
			.replace(/{mortgageAmountStartFixedInterestRate}/g, numberToCurrency(mortgageAmountStartFixedInterestRate, true))
			.replace(/{mortgageTermStart}/g, mortgageTermStart)
			.replace(/{residualDebt}/g, numberToCurrency(residualDebt, true))
			.replace(/{durationTermString}/g, durationTermString)
			.replace(/{endDate}/g, convertDateObject(parseISO(endDate), true))
	}
	return (
		<Fragment>
			{remainingMortgagePart &&
      <InputHolder tooltip={getToolTip(
				getMortgageForm(parseInt(values[formItems.step2.mortgagePart.mortgageType]), true),
				values[formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate],
				values[formItems.step2.mortgagePart.mortgageTermStart],
				remainingMortgagePart.residualDebt,
				remainingMortgagePart.durationTermString,
				remainingMortgagePart.endDate)}>
        <InputHolderItem isFullWidth={true}>
          <Select
          label={`Gewenste rente voor de ${getMortgageForm(parseInt(values[formItems.step2.mortgagePart.mortgageType]), true)} van ${numberToCurrency(remainingMortgagePart?.residualDebt, true)}*`}
          name={getFormName(formItems.step4.lowestInterest)}
          id={getFormName(formItems.step4.lowestInterest)}
          register={register}
          validation={{
						required: true,
						onChange: (e: any) =>
							setShowExtraField(e.currentTarget.value === 'OTHER_INTEREST'),
					}}
          options={interests}
          />
        </InputHolderItem>
      </InputHolder>
			}
			{showExtraField &&
      <InputHolder>
        <InputHolderItem>
          <Input
          label="Hypotheekrente"
          name={getFormName(formItems.step4.customInterest)}
          id={getFormName(formItems.step4.customInterest)}
          type="percentage"
          hasError={hasError(formItems.step4.customInterest)}
          placeholder="2.7%"
          register={register}
          validation={{required: true, validate: {
							notAPercentage: (v: string) => isPercentage(v)
						}
					}}
          />
					{errors[formItems.step4.customInterest] && (
						<span className="form__error">Vul de looptijd in jaren</span>
					)}
        </InputHolderItem>
      </InputHolder>
			}
		</Fragment>
	)
}
